import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Container, Form, Button, Alert, Row, Col, Card } from 'react-bootstrap';
import api from "../../../api/axiosConfig";
import { useAuth } from '../../../context/AuthContext';
import { CKEditor } from '@ckeditor/ckeditor5-react';
import ClassicEditor from '@ckeditor/ckeditor5-build-classic';
import './PostForm.css';

const CreatePost = () => {
    const navigate = useNavigate();
    const [title, setTitle] = useState('');
    const [category, setCategory] = useState('');
    const [featured, setFeatured] = useState(false);
    const [eventDate, setEventDate] = useState('');
    const [status, setStatus] = useState(0);
    const [error, setError] = useState('');
    const [successMessage, setSuccessMessage] = useState('');
    const [loading, setLoading] = useState(false);
    const { user } = useAuth();
    const [post, setPost] = useState('');
    const [pictureCaption, setPictureCaption] = useState('');
    const [selectedFile, setSelectedFile] = useState(null);
    const [imagePreview, setImagePreview] = useState('');
    const [uploadProgress, setUploadProgress] = useState(0);

    // Handle file selection
    const handleFileSelect = (e) => {
        const file = e.target.files[0];
        if (file) {
            // Validate file type based on your controller (jpg, png only)
            if (!file.type.match('image/jpeg') && !file.type.match('image/png')) {
                setError('Please select a valid JPG or PNG image file');
                return;
            }

            // Validate file size (max 2MB as per your controller)
            if (file.size > 2 * 1024 * 1024) {
                setError('Image size should be less than 2MB');
                return;
            }

            setSelectedFile(file);
            setError('');

            // Create preview
            const reader = new FileReader();
            reader.onload = (e) => {
                setImagePreview(e.target.result);
            };
            reader.readAsDataURL(file);
        }
    };

    // Upload image to server - matches your PictureController store method
    const uploadImage = async (file, postId) => {
        const formData = new FormData();
        formData.append('picture_files', file); // Matches your controller validation
        formData.append('caption', pictureCaption);
        formData.append('post_id', postId); // Required for your controller

        try {
            const response = await api.post('/pictures', formData, {
                headers: {
                    'Content-Type': 'multipart/form-data',
                },
                onUploadProgress: (progressEvent) => {
                    const progress = Math.round(
                        (progressEvent.loaded * 100) / progressEvent.total
                    );
                    setUploadProgress(progress);
                },
            });

            console.log('Upload response:', response.data);

            if (response.data.success) {
                return response.data.data; // Your API returns the path in data field
            } else {
                throw new Error(response.data.message || 'Failed to upload image');
            }
        } catch (err) {
            console.error('Image upload error:', err.response?.data || err.message);
            throw new Error(err.response?.data?.message || 'Failed to upload image');
        }
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        setLoading(true);
        setError('');
        setSuccessMessage('');
        setUploadProgress(0);

        try {
            const siteId = user?.site_id;
            if (!siteId) {
                setError('Site ID not found. Please contact support.');
                setLoading(false);
                return;
            }

            // First create the post
            const postData = {
                site_id: siteId,
                title: title,
                post: post,
                category: category,
                featured: featured ? 1 : 0,
                event_date: eventDate,
                status: status,
                created_by: user.id,
                updated_by: user.id,
            };

            console.log('Creating post with data:', postData);

            const postResponse = await api.post('/posts', postData);

            console.log('Post creation response:', postResponse.data);

            if (postResponse.data.success) {
                const postId = postResponse.data.data?.id || postResponse.data.postId;

                // Upload image if selected and associate with the post
                if (selectedFile && postId) {
                    try {
                        await uploadImage(selectedFile, postId);
                    } catch (uploadError) {
                        console.warn('Image upload failed but post was created:', uploadError.message);
                        // Continue even if image upload fails - post is already created
                    }
                }

                setSuccessMessage('Post created successfully!');

                // Reset form
                setTitle('');
                setPost('');
                setCategory('');
                setFeatured(false);
                setEventDate('');
                setStatus(0);
                setPictureCaption('');
                setSelectedFile(null);
                setImagePreview('');
                setUploadProgress(0);

                // Navigate after a short delay to show success message
                setTimeout(() => {
                    navigate('/site-admin/posts');
                }, 2000);
            } else {
                setError(postResponse.data.message || 'Failed to create post.');
            }
        } catch (err) {
            console.error('Create post error:', err.response?.data || err.message);
            setError(err.response?.data?.message || 'An error occurred while creating the post.');
        } finally {
            setLoading(false);
        }
    };

    const handleEditorChange = (event, editor) => {
        const data = editor.getData();
        setPost(data);
    };

    // Remove selected image
    const removeImage = () => {
        setSelectedFile(null);
        setImagePreview('');
    };

    return (
        <Container className="create-post-container">
            <div className="create-post-header">
                <h1>Create New Post</h1>
            </div>

            {error && <Alert className="alert-custom alert-danger-custom">{error}</Alert>}
            {successMessage && <Alert className="alert-custom alert-success-custom">{successMessage}</Alert>}

            <Form onSubmit={handleSubmit} className="create-post-form">
                <Row>
                    <Col md={8}>
                        <div className="form-section">
                            <h3 className="form-section-title">Post Information</h3>

                            <div className="form-group-custom">
                                <label className="form-label-custom">Title *</label>
                                <Form.Control
                                    type="text"
                                    placeholder="Enter title"
                                    value={title}
                                    onChange={(e) => setTitle(e.target.value)}
                                    required
                                    className="form-control-custom"
                                />
                            </div>

                            <div className="form-group-custom">
                                <label className="form-label-custom">Post Content</label>
                                <div className="ckeditor-container">
                                    <CKEditor
                                        editor={ClassicEditor}
                                        data={post}
                                        onChange={handleEditorChange}
                                        config={{
                                            toolbar: ['heading', '|', 'bold', 'italic', 'link', 'bulletedList', 'numberedList', 'blockQuote', 'insertTable', 'undo', 'redo']
                                        }}
                                    />
                                </div>
                            </div>

                            <Row>
                                <Col md={6}>
                                    <div className="form-group-custom">
                                        <label className="form-label-custom">Category</label>
                                        <Form.Control
                                            type="text"
                                            placeholder="Enter category"
                                            value={category}
                                            onChange={(e) => setCategory(e.target.value)}
                                            className="form-control-custom"
                                        />
                                    </div>
                                </Col>
                                <Col md={6}>
                                    <div className="form-group-custom">
                                        <label className="form-label-custom">Event Date</label>
                                        <Form.Control
                                            type="datetime-local"
                                            value={eventDate}
                                            onChange={(e) => setEventDate(e.target.value)}
                                            className="form-control-custom"
                                        />
                                    </div>
                                </Col>
                            </Row>

                            <Row>
                                <Col md={6}>
                                    <div className="status-container">
                                        <label className="form-label-custom">Status</label>
                                        <Form.Control
                                            as="select"
                                            value={status}
                                            onChange={(e) => setStatus(parseInt(e.target.value))}
                                            className="form-control-custom"
                                        >
                                            <option value={0}>Draft</option>
                                            <option value={1}>Published</option>
                                        </Form.Control>
                                        <span className={`status-indicator ${status === 1 ? 'status-published' : 'status-draft'}`}>
                                            {status === 1 ? 'Published' : 'Draft'}
                                        </span>
                                    </div>
                                </Col>
                                <Col md={6}>
                                    <div className="featured-container">
                                        <div className="featured-checkbox">
                                            <input
                                                type="checkbox"
                                                checked={featured}
                                                onChange={(e) => setFeatured(e.target.checked)}
                                            />
                                            <span className="featured-label">Featured Post</span>
                                        </div>
                                    </div>
                                </Col>
                            </Row>
                        </div>
                    </Col>

                    <Col md={4}>
                        <Card className="featured-image-card">
                            <Card.Header className="card-header-custom">
                                <h5 className="mb-0">Featured Image</h5>
                            </Card.Header>
                            <Card.Body className="card-body-custom">
                                <div className="form-group-custom">
                                    <label className="form-label-custom">Upload Image</label>
                                    <div className="custom-file-input">
                                        <Form.Control
                                            type="file"
                                            accept="image/jpeg, image/png"
                                            onChange={handleFileSelect}
                                        />
                                        <div className={`file-input-label ${selectedFile ? 'has-file' : ''}`}>
                                            {selectedFile ? selectedFile.name : 'Choose JPG or PNG file (max 2MB)'}
                                        </div>
                                    </div>
                                    <Form.Text className="text-muted">
                                        Supported formats: JPG, PNG only. Max size: 2MB
                                    </Form.Text>
                                </div>

                                {uploadProgress > 0 && uploadProgress < 100 && (
                                    <div className="upload-progress">
                                        <div className="progress-info">
                                            <small>Uploading...</small>
                                            <small>{uploadProgress}%</small>
                                        </div>
                                        <div className="progress-bar-custom">
                                            <div
                                                className="progress-bar-fill"
                                                style={{ width: `${uploadProgress}%` }}
                                            ></div>
                                        </div>
                                    </div>
                                )}

                                <div className="form-group-custom">
                                    <label className="form-label-custom">Image Caption</label>
                                    <Form.Control
                                        type="text"
                                        placeholder="Enter image caption"
                                        value={pictureCaption}
                                        onChange={(e) => setPictureCaption(e.target.value)}
                                        className="form-control-custom"
                                    />
                                </div>

                                {imagePreview && (
                                    <div className="image-preview-container">
                                        <div className="image-preview">
                                            <img
                                                src={imagePreview}
                                                alt="Preview"
                                            />
                                            <button
                                                className="remove-image-btn"
                                                onClick={removeImage}
                                                title="Remove image"
                                            >
                                                ×
                                            </button>
                                        </div>
                                    </div>
                                )}
                            </Card.Body>
                        </Card>
                    </Col>
                </Row>

                <div className="form-actions">
                    <Button
                        variant="primary"
                        type="submit"
                        disabled={loading}
                        className="btn-primary-custom"
                    >
                        {loading ? (
                            <>
                                <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>
                                Creating...
                            </>
                        ) : (
                            'Create Post'
                        )}
                    </Button>
                    <Button
                        variant="secondary"
                        onClick={() => navigate('/site-admin/posts')}
                        className="btn-secondary-custom"
                    >
                        Cancel
                    </Button>
                </div>
            </Form>
        </Container>
    );
};

export default CreatePost;